<!-- The original Perspective circle answer row, shown while the perspective_survey_slider flag is off. Delete it when the flag is retired. -->
<template>
    <BaseRadioGroup
        appearance="scale"
        :model-value="modelValue"
        :options="options"
        :disabled="disabled"
        :aria-label="label"
        :label-start="t('components.likertScale.disagree')"
        :label-end="t('components.likertScale.agree')"
        @update:model-value="(v: AcceptableValue) => emit('update:modelValue', Number(v))"
        @keydown.capture="handleKeydown"
        @click.capture="handleClick" />
</template>

<script lang="ts">
import { i18n } from "~vue/i18n";

export interface Props {
    modelValue?: number;
    disabled?: boolean;
    label?: string;
}

/** Exported so a caller can enumerate the valid answers rather than restating the scale. */
export const SCALE_POINTS = [
    { value: -3, label: () => i18n.global.t("components.likertScale.stronglyDisagree") },
    { value: -2, label: () => i18n.global.t("components.likertScale.disagree") },
    { value: -1, label: () => i18n.global.t("components.likertScale.slightlyDisagree") },
    { value: 0, label: () => i18n.global.t("components.likertScale.neutral") },
    { value: 1, label: () => i18n.global.t("components.likertScale.slightlyAgree") },
    { value: 2, label: () => i18n.global.t("components.likertScale.agree") },
    { value: 3, label: () => i18n.global.t("components.likertScale.stronglyAgree") },
] as const;
</script>

<script setup lang="ts">
/**
 * The seven point agreement scale, as BaseRadioGroup's scale appearance. This wrapper owns the
 * points and their copy, the numeric value, and the `advance` event survey pages use to move
 * focus to the next question once a point is picked.
 */
import BaseRadioGroup, { type RadioGroupOption } from "~vue/components/form/BaseRadioGroup.vue";
import type { AcceptableValue } from "reka-ui";
import { computed, nextTick } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

defineProps<Props>();

const emit = defineEmits<{
    "update:modelValue": [value: number];
    advance: [columnIndex: number];
}>();

const options = computed<RadioGroupOption[]>(() => SCALE_POINTS.map((point) => ({ value: point.value, label: point.label() })));

function columnIndexOf(radio: Element): number {
    const value = radio.getAttribute("value");
    const columnIndex = value === null ? -1 : SCALE_POINTS.findIndex((point) => point.value === Number(value));
    return columnIndex >= 0 ? columnIndex : 0;
}

function handleKeydown(event: KeyboardEvent) {
    if (event.key !== "Enter" && event.key !== " ") {
        return;
    }
    const target = event.target as HTMLElement;
    if (target.getAttribute("role") !== "radio") {
        return;
    }
    event.preventDefault();
    event.stopPropagation();
    const value = target.getAttribute("value");
    if (value === null) {
        return;
    }
    emit("update:modelValue", Number(value));
    nextTick(() => emit("advance", columnIndexOf(target)));
}

function handleClick(event: MouseEvent) {
    // reka-ui fires a programmatic click on arrow key, this blocks unless its from a user agent
    if (!event.isTrusted) {
        return;
    }
    const target = (event.target as HTMLElement).closest('[role="radio"]');
    if (!target) {
        return;
    }
    nextTick(() => emit("advance", columnIndexOf(target)));
}
</script>
